import { PLAYLIST_PANE } from '@disco/constants/PaneTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  alterPlaylistGroups,
  removeTracksFromPlaylist,
  updateUserSettingsUnsafe,
} from '@disco/state-management/actions/ExplorerActions'
import { resetSelection } from '@disco/state-management/actions/SelectionActions'
import {
  bulkDeletePlaylists,
  bulkDeleteTracks,
} from '@disco/state-management/actions/ServerActions'
import { getGroups } from '@disco/state-management/selectors/groups'
import {
  getExplorerSelectedPane,
  getExplorerSelectedPlaylists,
  getExplorerSelectedTracks,
} from '@disco/state-management/selectors/selection'
import { getHideInboxRemovalSetting } from '@disco/state-management/selectors/user'
import * as React from 'react'
import { useSelector, useDispatch } from 'hooks/use-redux'
import { useNavigate, useParams } from 'react-router-dom'
import type { ExplorerPane } from 'types/explorer'
import DocumentHeadTags from 'components/document-head-tags'
import InboxRemovalDialog from 'components/modals/dialogs/inbox-removal-dialog'
import type { Playlist } from 'types/playlist'
import type { Track } from 'types/track'

function SelectionRemove() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { inboxId } = useParams<{ inboxId: string }>()

  const selectedTracks = useSelector(getExplorerSelectedTracks)
  const selectedPlaylists = useSelector(getExplorerSelectedPlaylists)
  const groups = useSelector(getGroups)
  const hideRemovalWarning = useSelector(getHideInboxRemovalSetting)
  const settings = useSelector((state: any) => state.user.settings)
  const selectedPane = useSelector(getExplorerSelectedPane)

  const onClose = () => {
    navigate('/')
  }

  const handleDelete = () => {
    const selectedLibrary = groups.find(g => g.id === parseInt(inboxId!, 10))
    const tracksToRemove = selectedTracks.filter(
      (t: Track) => t.groups && t.groups.length > 1,
    )
    const tracksToDelete = selectedTracks.filter(
      (t: Track) =>
        t.groups &&
        t.groups.length === 1 &&
        selectedLibrary &&
        selectedLibrary.url === t.groups[0],
    )
    const playlistsToRemove = selectedPlaylists.filter(
      (p: Playlist) => p.groups && p.groups.length > 1,
    )
    const playlistsToDelete = selectedPlaylists.filter(
      (p: Playlist) =>
        p.groups &&
        p.groups.length === 1 &&
        selectedLibrary &&
        selectedLibrary.url === p.groups[0],
    )

    if (playlistsToDelete.length) {
      dispatch(
        bulkDeletePlaylists(
          playlistsToDelete.map((p: Playlist) => p.playlist_id),
        ),
      )
    }

    if (tracksToDelete.length) {
      dispatch(
        bulkDeleteTracks(
          tracksToDelete.map((t: Track) => t.id),
          'Remove from DISCO action inside inbox playlist',
          null,
        ),
      )
    }

    if (playlistsToRemove.length) {
      dispatch(alterPlaylistGroups(playlistsToRemove, [], [selectedLibrary]))
    }

    if (tracksToRemove.length) {
      if (selectedPane?.type === PLAYLIST_PANE && selectedPane?.playlist_id) {
        dispatch(
          removeTracksFromPlaylist(
            {
              playlist_id: selectedPane?.playlist_id,
            },
            tracksToRemove,
          ),
        )
      }
    }

    if (tracksToRemove.length || tracksToDelete.length) {
      dispatch(sendMixpanelEvent('Inbox reject multiple tracks'))
    }

    if (playlistsToRemove.length || playlistsToDelete.length) {
      dispatch(sendMixpanelEvent('Inbox remove multiple playlists'))
    }

    dispatch(resetSelection())
    navigate('/')
  }

  const hideWarning = () => {
    dispatch(
      updateUserSettingsUnsafe({
        settings: {
          hide_inbox_removal_warning: !hideRemovalWarning,
        },
      }),
    )
  }

  const type = selectedPlaylists.length ? 'playlist' : 'track'

  return (
    <div>
      <DocumentHeadTags title="Remove from inbox | DISCO" />
      <InboxRemovalDialog
        onConfirm={handleDelete}
        onCancel={onClose}
        onHideWarningChange={hideWarning}
        hideWarning={hideRemovalWarning}
        multipleItems
        type={type}
      />
    </div>
  )
}

export default SelectionRemove
